import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/components/Toast";
import { AttachmentPicker } from "@/components/AttachmentPicker";
import { isOperationalRole } from "@/lib/roles";
import { ClipboardCheck, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Gate bloqueante del checklist de SERVICIO al iniciar el turno (distinto de
 * "Elementos de Trabajo" que es el kit personal). Se muestra al personal
 * operativo (VIGILADOR_ROLES) DESPUÉS de que no queden cuestionarios
 * obligatorios pendientes (QuizGate). Se monta globalmente junto a QuizGate.
 */
export default function ServiceChecklistGate() {
  const { user } = useAuth();
  const [checklist, setChecklist] = useState(null);
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const pollRef = useRef(null);

  const checkPending = useCallback(async () => {
    try {
      const { data: quizData } = await api.get("/quizzes/pending");
      if (quizData.quiz) { setChecklist(null); return; } // esperá a que termine el cuestionario primero
    } catch { /* silencioso */ }
    try {
      const { data } = await api.get("/checklist/pending");
      if (data.pending) {
        setChecklist(data);
        const init = {};
        data.items.forEach((it) => { init[it.id] = { completed: true, observation: "", photo: [] }; });
        setResults(init);
      } else {
        setChecklist(null);
      }
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    if (user && isOperationalRole(user.role)) {
      checkPending();
      pollRef.current = setInterval(checkPending, 5000);
    }
    return () => clearInterval(pollRef.current);
  }, [user, checkPending]);

  useEffect(() => {
    if (checklist && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, [checklist]);

  if (!user || !isOperationalRole(user.role) || !checklist) return null;

  const setItem = (id, patch) => setResults((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submit = async () => {
    setBusy(true);
    try {
      const items = checklist.items.map((it) => ({
        item_id: it.id, text: it.text,
        completed: !!results[it.id]?.completed,
        observation: results[it.id]?.observation || null,
        photo_url: results[it.id]?.photo?.[0]?.url || null,
      }));
      await api.post("/checklist/complete", { client_id: checklist.client_id, site_id: checklist.site_id, items });
      setDone(true);
      toast.success("Checklist de servicio completado");
      setTimeout(() => { setDone(false); setChecklist(null); }, 1400);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="service-checklist-gate" style={{ position: "fixed", inset: 0, zIndex: 9998, background: "#0A0A0A", overflowY: "auto", color: "#FFFBF4" }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: "32px 18px 60px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <ClipboardCheck size={22} color="#BF3437" />
          <span style={{ fontSize: 11, fontWeight: 900, letterSpacing: "0.18em", textTransform: "uppercase", color: "#BF3437" }}>Checklist de servicio</span>
        </div>
        <h1 style={{ fontSize: 22, fontWeight: 900, margin: "0 0 4px", letterSpacing: "-0.02em" }} data-testid="service-checklist-title">{checklist.client_name}{checklist.site_name ? ` · ${checklist.site_name}` : ""}</h1>
        <p style={{ fontSize: 12, color: "rgba(255,251,244,0.5)", marginBottom: 22 }}>
          Antes de empezar tu turno en este puesto, revisá y confirmá estos ítems.
        </p>

        {done ? (
          <div style={{ textAlign: "center", padding: 40 }} data-testid="service-checklist-done">
            <CheckCircle2 size={48} color="#16a34a" style={{ margin: "0 auto" }} />
            <div style={{ fontSize: 16, fontWeight: 800, marginTop: 12 }}>Listo, buen turno</div>
          </div>
        ) : (
          <>
            {checklist.items.map((it, i) => {
              const r = results[it.id] || {};
              return (
                <div key={it.id} style={{ background: "rgba(255,251,244,0.05)", borderRadius: 12, padding: 16, marginBottom: 12 }} data-testid={`service-checklist-item-${it.id}`}>
                  <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 10 }}>{i + 1}. {it.text}</div>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    <button type="button" onClick={() => setItem(it.id, { completed: true })} data-testid={`service-checklist-ok-${it.id}`}
                      style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `2px solid ${r.completed ? "#16a34a" : "rgba(255,251,244,0.15)"}`, background: r.completed ? "rgba(22,163,74,0.18)" : "transparent", color: "#FFFBF4", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                      Cumplido
                    </button>
                    <button type="button" onClick={() => setItem(it.id, { completed: false })} data-testid={`service-checklist-fail-${it.id}`}
                      style={{ flex: 1, padding: "9px 0", borderRadius: 8, border: `2px solid ${r.completed === false ? "#BF3437" : "rgba(255,251,244,0.15)"}`, background: r.completed === false ? "rgba(191,52,55,0.18)" : "transparent", color: "#FFFBF4", fontWeight: 800, fontSize: 12, cursor: "pointer" }}>
                      No cumplido
                    </button>
                  </div>
                  <textarea
                    value={r.observation || ""}
                    onChange={(e) => setItem(it.id, { observation: e.target.value })}
                    placeholder="Observación (opcional)"
                    data-testid={`service-checklist-obs-${it.id}`}
                    style={{ width: "100%", minHeight: 46, background: "rgba(255,251,244,0.08)", border: "1px solid rgba(255,251,244,0.15)", borderRadius: 6, color: "#FFFBF4", fontSize: 12, padding: 8, marginBottom: 8, resize: "vertical" }}
                  />
                  <AttachmentPicker value={r.photo || []} onChange={(v) => setItem(it.id, { photo: v })} accept="image" multiple={false} max={1} label="Foto" />
                </div>
              );
            })}
            <button onClick={submit} disabled={busy} className="btn-primary" style={{ width: "100%", padding: 14, marginTop: 8 }} data-testid="service-checklist-submit">
              {busy ? <Loader2 size={16} className="spin" style={{ display: "inline", marginRight: 6 }} /> : null}{busy ? "Enviando..." : "Confirmar checklist"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
